import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon, CameraIcon } from './icons';
import { addUserLicense, updateUserLicense } from '../services/licenses';
import { uploadImage } from '../services/imageUpload';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';

interface AddLicenseModalProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onLicenseCreated?: () => void | Promise<void>;
}

const isBlobUrl = (url: string | null | undefined) =>
  typeof url === 'string' && url.startsWith('blob:');

const AddLicenseModal: React.FC<AddLicenseModalProps> = ({
  userId,
  isOpen,
  onClose,
  onLicenseCreated,
}) => {
  const { t } = useTranslation();
  const isDark = resolveRoddoDarkMode();
  const ui = getRoddoUiTokens(isDark) as any;

  const [jurisdictionCountry, setJurisdictionCountry] = useState('US');
  const [jurisdictionState, setJurisdictionState] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [holderName, setHolderName] = useState('');
  const [waterType, setWaterType] = useState<'freshwater' | 'saltwater' | 'both'>('freshwater');
  const [tier, setTier] = useState('Sport fishing');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const canSubmit = useMemo(
    () => !!userId && !!jurisdictionState.trim() && !!licenseNumber.trim() && !!holderName.trim(),
    [userId, jurisdictionState, licenseNumber, holderName],
  );

  const revokePreview = (url: string | null) => {
    if (isBlobUrl(url)) {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    }
  };

  const resetForm = () => {
    setJurisdictionCountry('US');
    setJurisdictionState('');
    setLicenseNumber('');
    setHolderName('');
    setWaterType('freshwater');
    setTier('Sport fishing');
    setStartDate('');
    setEndDate('');
    setNotes('');
    setPhotoFile(null);
    setPhotoPreviewUrl((prev) => {
      revokePreview(prev);
      return null;
    });
    setError(null);
  };

  useEffect(() => {
    if (!isOpen) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => revokePreview(photoPreviewUrl);
  }, [photoPreviewUrl]);

  if (!isOpen) return null;

  const handlePickPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      setError(t('licenses.photoTypeError', 'Please choose an image file.'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError(t('licenses.photoSizeError', 'Photo must be under 10MB.'));
      return;
    }

    const nextPreview = URL.createObjectURL(file);
    setPhotoPreviewUrl((prev) => {
      revokePreview(prev);
      return nextPreview;
    });
    setPhotoFile(file);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (!jurisdictionState.trim() || !licenseNumber.trim() || !holderName.trim()) {
      setError(
        t('licenses.requiredFields', 'Please fill in state/region, license number, and holder name.'),
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const now = Date.now();
      const startTs = startDate ? new Date(startDate).getTime() : now;
      const endTs = endDate ? new Date(endDate).getTime() : undefined;

      const licenseId = await addUserLicense(userId, {
        jurisdictionCountry: jurisdictionCountry.trim() || 'US',
        jurisdictionState: jurisdictionState.trim(),
        state: jurisdictionState.trim(),
        licenseNumber: licenseNumber.trim(),
        nameOnLicense: holderName.trim(),
        holderName: holderName.trim(),
        waterType,
        tier: tier.trim() || 'Sport fishing',
        type: tier.trim() || 'Sport fishing',
        purchasedAt: now,
        startDate: startTs,
        endDate: endTs,
        expiryDate: endTs,
        status: 'active',
        active: true,
        notes: notes.trim() || undefined,
        createdAt: now,
        updatedAt: now,
      } as any);

      if (!licenseId) throw new Error('License ID was not returned from addUserLicense.');

      if (photoFile) {
        const photoUrl = await uploadImage(photoFile, `licenses/${userId}/${licenseId}.jpg`, {
          maxSizeMB: 1.2,
          maxDim: 1600,
          contentType: 'image/jpeg',
        });

        await updateUserLicense(userId, licenseId, {
          photoUrl,
          licensePhotoUrl: photoUrl,
        } as any);
      }

      if (onLicenseCreated) await onLicenseCreated();
      resetForm();
      onClose();
    } catch (err) {
      console.error('[AddLicenseModal] Failed to create license:', err);
      setError(t('licenses.createError', 'Failed to save license. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const shellStyle: React.CSSProperties = {
    background: ui.cardBgStrong ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    boxShadow: `${ui.shadowStrong ?? ui.shadow}, inset 0 1px 0 rgba(255,255,255,0.18)`,
    color: ui.textPrimary,
  };
  const sectionStyle: React.CSSProperties = {
    background: ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: `${ui.insetHighlight}`,
  };
  const inputStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.inputBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    color: ui.textPrimary,
    boxShadow: `${ui.insetHighlight}`,
  };
  const labelStyle: React.CSSProperties = { color: ui.textSecondary };
  const subtleButtonStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    color: ui.textPrimary,
    boxShadow: `0 5px 14px rgba(89,65,37,0.08), ${ui.insetHighlight}`,
  };
  const primaryButtonStyle: React.CSSProperties = {
    background: isDark ? 'linear-gradient(180deg, #66D6A9, #42B88B)' : 'linear-gradient(180deg, #2F7DF4, #2563EB)',
    color: '#FFFFFF',
    boxShadow: isDark ? '0 8px 20px rgba(42,150,110,0.24)' : '0 8px 18px rgba(37,99,235,0.20)',
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-[26px]" style={shellStyle} onClick={(e) => e.stopPropagation()}>
        <div
          className="flex items-center justify-between border-b px-4 py-3"
          style={{ borderColor: ui.border }}
        >
          <h2 className="text-lg font-extrabold" style={{ color: ui.textPrimary }}>
            {t('licenses.addTitle', 'Add fishing license')}
          </h2>

          <button
            type="button"
            onClick={handleClose}
            className="grid h-10 w-10 place-items-center rounded-[14px] p-0"
            style={subtleButtonStyle}
            aria-label={t('common.close', 'Close') as string}
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {error ? (
            <div
              className="rounded-xl p-2 text-xs"
              style={{ background: isDark ? 'rgba(248,113,113,0.16)' : '#fee2e2', color: '#b91c1c' }}
            >
              {error}
            </div>
          ) : null}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold" style={labelStyle}>
                {t('licenses.photo', 'License photo (optional)')}
              </label>

              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="inline-flex min-h-9 items-center gap-2 rounded-[14px] px-3 py-1.5 text-xs font-semibold"
                style={subtleButtonStyle}
              >
                <CameraIcon className="h-4 w-4" />
                {photoPreviewUrl
                  ? t('licenses.changePhoto', 'Change photo')
                  : t('licenses.addPhoto', 'Add photo')}
              </button>
            </div>

            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePickPhoto}
            />

            {photoPreviewUrl ? (
              <div className="overflow-hidden rounded-2xl" style={{ border: `1px solid ${ui.border}` }}>
                <img src={photoPreviewUrl} alt="License preview" className="h-40 w-full object-cover" />
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed p-4 text-xs" style={{ ...sectionStyle, color: ui.textSecondary }}>
                {t(
                  'licenses.photoHint',
                  'Snap a quick license photo now, or add one later from the detail card.',
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
                {t('licenses.country', 'Country')}
              </label>
              <input
                type="text"
                className="w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
                style={inputStyle}
                value={jurisdictionCountry}
                onChange={(e) => setJurisdictionCountry(e.target.value)}
                placeholder="US"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
                {t('licenses.state', 'State / Region')}
              </label>
              <input
                type="text"
                className="w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
                style={inputStyle}
                value={jurisdictionState}
                onChange={(e) => setJurisdictionState(e.target.value)}
                placeholder="CA, WA, etc."
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
              {t('licenses.licenseNumber', 'License number')}
            </label>
            <input
              type="text"
              className="w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
              style={inputStyle}
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              placeholder="1234-5678-ABCD"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
              {t('licenses.holderName', 'Name on license')}
            </label>
            <input
              type="text"
              className="w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
              style={inputStyle}
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              placeholder={t('licenses.holderPlaceholder', 'Your full name')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
                {t('licenses.waterType', 'Water type')}
              </label>
              <select
                className="w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
                style={inputStyle}
                value={waterType}
                onChange={(e) => setWaterType(e.target.value as 'freshwater' | 'saltwater' | 'both')}
              >
                <option value="freshwater">{t('licenses.freshwater', 'Freshwater')}</option>
                <option value="saltwater">{t('licenses.saltwater', 'Saltwater')}</option>
                <option value="both">{t('licenses.both', 'Both')}</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
                {t('licenses.tier', 'Tier / Type')}
              </label>
              <input
                type="text"
                className="w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
                style={inputStyle}
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                placeholder={t('licenses.tierPlaceholder', 'Sport fishing')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
                {t('licenses.startDate', 'Start date')}
              </label>
              <input
                type="date"
                className="w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
                style={inputStyle}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
                {t('licenses.endDate', 'End date')}
              </label>
              <input
                type="date"
                className="w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
                style={inputStyle}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold" style={labelStyle}>
              {t('licenses.notes', 'Notes (optional)')}
            </label>
            <textarea
              className="min-h-[84px] w-full rounded-[14px] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#6C63FF]/20"
              style={inputStyle}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t(
                'licenses.notesPlaceholder',
                'Special endorsements, restrictions, or anything worth remembering.',
              )}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="min-h-10 rounded-[14px] px-4 py-2 text-sm font-semibold"
              style={subtleButtonStyle}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </button>

            <button
              type="submit"
              disabled={isSubmitting || !canSubmit}
              className="min-h-10 rounded-[14px] px-4 py-2 text-sm font-semibold transition-[transform,filter] hover:-translate-y-px active:translate-y-0 active:scale-[0.98] disabled:opacity-60"
              style={primaryButtonStyle}
            >
              {isSubmitting ? t('licenses.saving', 'Saving…') : t('licenses.save', 'Save license')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLicenseModal;
